import { TOAST_MARGIN_LEFT } from 'constants/editor';
import { useToast } from 'modules/common/components/Toast';
import { useCallback } from 'react';

export interface useToastProps {
  successToast: string;
  failToast: string;
}

export const useToastHelpers = ({ successToast, failToast }: useToastProps) => {
  const toast = useToast({
    position: 'bottom-left',
    containerStyle: {
      marginLeft: TOAST_MARGIN_LEFT,
    },
  });

  const showSuccessToast = useCallback(
    (title: string) => {
      toast({
        title: title,
        status: 'success',
        duration: 3000,
        isClosable: false,
      });
    },
    [toast],
  );

  const showFailedToast = useCallback(
    (title: string, description?: string) => {
      const descriptionObj = description ? { description } : null;
      toast({
        title,
        status: 'error',
        duration: 3000,
        isClosable: false,
        ...descriptionObj,
      });
    },
    [toast],
  );

  const copyTextToClipboard = useCallback(
    (text: string) => {
      if (window.isSecureContext && navigator.clipboard) {
        // Copy to clipborad using navigator.clipboard browser API
        navigator.clipboard.writeText(text).then(
          () => showSuccessToast(successToast),
          () => showFailedToast(failToast),
        );
      } else {
        // Copy to clipboard for unsecure URL such as localhost or venngage.local
        try {
          const element = document.createElement('span');
          element.textContent = text;
          document.body.appendChild(element);
          const selection = window.getSelection();
          const range = document.createRange();
          if (selection) {
            selection.removeAllRanges();
            range.selectNode(element);
            selection.addRange(range);
            document.execCommand('copy');
            selection.removeAllRanges();
            document.body.removeChild(element);
            showSuccessToast(successToast);
          } else {
            showFailedToast(failToast);
          }
        } catch (error) {
          showFailedToast(failToast);
        }
      }
    },
    [showSuccessToast, showFailedToast, failToast, successToast],
  );

  return {
    copyTextToClipboard,
    showSuccessToast,
    showFailedToast,
  };
};
